<script>
  /* global Stripe */
  import {createEventDispatcher, onDestroy, onMount} from "svelte";
  import Spinner from "svelte-spinner";
  import { _, locale, number } from "svelte-i18n";
  import Actions from "@components/Actions";
  import Button from "@components/Button";
  import I18nHtml from "@components/I18nHtml";
  import TextButton from "@components/TextButton";
  import { authenticatedFetch, currentUser } from "@stores/auth";
  import { experience } from "@stores/global";
  import { notify } from "@stores/notification";

  export let amount;
  export let currency;

  const dispatch = createEventDispatcher();

  let stripeReady = false;
  let mounted = false;
  let loading = true;
  let error = "";
  let card;
  let clientSecret;
  let stripe;
  let unsubscribeSnapshot;
  let working = false;

  onMount(() => {
    mounted = true;

    if (document.querySelector("#stripev3")) {
      onStripeLoad();
    } else {
      const script = document.createElement("script");
      script.async = true;
      script.id = "stripev3";
      script.onload = onStripeLoad;
      script.src = "https://js.stripe.com/v3/";
      document.head.appendChild(script);
    }
  });

  onDestroy(() => {
    if (card && typeof card.destroy === "function") {
      card.destroy();
    }
    if (typeof unsubscribeSnapshot === "function") {
      unsubscribeSnapshot();
    }
    working = false;
  });

  /**
   * Set flag when Stripe script loads
   */
  function onStripeLoad() {
    stripeReady = true;
  }

  $: formattedPrice = $number(amount / 100, {
    style: "currency",
    currency: currency || "gbp"
  });

  $: {
    if (mounted && stripeReady) {
      createPaymentIntent()
        .then(loadStripeElements)
        .catch((err) => {
          error = err.message;
          loading = false;

          import(
            /* webpackChunkName: "sentry-browser" */
            "@sentry/browser"
          ).then((Sentry) => {
            Sentry.captureException(err, {
              tags: {
                functionality: "PaymentForm"
              }
            });

            dispatch("close");
            notify(
              $_("errors.generic", {
                default: "Something went wrong, please try again"
              })
            );
          });
        });
    }
  }

  /**
   * Creates a Stripe payment intent and returns private
   * keys to client
   *
   * @returns {Promise<object>} data
   */
  function createPaymentIntent() {
    return authenticatedFetch("/payments/create-payment-intent", {
      method: "POST",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        amount,
        currency,
        experienceId: $experience.id
      })
    });
  }

  /**
   * Set up Stripe elements and mount card
   *
   * @param {object} data
   * @param {string} data.clientSecret Secret to communicate with Stripe
   * @param {string} data.publishableKey Stripe API key
   */
  function loadStripeElements(data) {
    if (!stripe) {
      stripe = new Stripe(data.publishableKey, {
        locale: $locale
      });
    }
    clientSecret = data.clientSecret;

    if (!clientSecret) {
      throw new Error("No client secret");
    }

    const elements = stripe.elements();
    const style = {
      base: {
        fontSize: "16px",
      },
      invalid: {},
    };
    card = elements.create("card", { style });
    card.mount("#card-element");
    card.on("change", (event) => {
      error = event.error ? event.error.message : "";
    });
    loading = false;
  }

  /**
   * Make the payment to Stripe
   */
  async function makePayment() {
    working = true;

    const response = await stripe.confirmCardPayment(clientSecret, {
      // eslint-disable-next-line camelcase
      payment_method: {
        card
      }
    });

    if (response.error) {
      working = false;
      error = response.error.message;

      setTimeout(() => {
        error = "";
      }, 4000);

      return;
    }

    const { default: firebaseClient } = await import(
      /* webpackChunkName: "firebase" */
      "@services/firebase"
    );
    const { firestore } = await firebaseClient();

    unsubscribeSnapshot = firestore
      .collection("stripeOrders")
      .where("firebaseUserId", "==", $currentUser.uid)
      .onSnapshot(
        (snapshot) => {
          if (snapshot.size > 0) {
            const {fulfilled} = snapshot.docs[0].data();

            if (fulfilled) {
              dispatch("success");
            }
          }
        });
  }

  /**
   * Dispatch a close event
   */
  function close() {
    dispatch("close");
  }
</script>

<style>
  .hidden {
    display:none
  }

  fieldset {
    border: 0;
    margin: 0;
    padding: 0;
  }

  #card-element {
    appearance: none;
    background-color: var(--primary-color);
    border: 1px solid var(--thin-shadow);
    border-radius: 0.25rem;
    display: block;
    font-size: 1.6rem;
    height: 100%;
    line-height: 1.25;
    margin: 0 0 1.6rem;
    padding: 1.35rem 1.6rem;
    text-align: left;
  }

  #card-errors {
    color: var(--error-color);
    font-size: 1.4rem;
    margin: 0 0 1.6rem;
  }

  .terms-conditions {
    font-size: 1.4rem;
    margin: 0 0 1.6rem;
  }
</style>

{#if loading}
  <Spinner size="50" color="var(--secondary-color)" />
{/if}

<form class:hidden={loading} id="payment-form" on:submit|preventDefault>
  <fieldset disabled={working}>
    <div id="card-element"></div>
    <div id="card-errors" role="alert">{error}</div>
    <p class="terms-conditions">
      <I18nHtml
        key="components.payment_form.terms_and_conditions.html"
        default={[
          "By making a purchase you accept Stripe’s",
          `'<strong><a
              href="https://stripe.com/en-gb/checkout/legal"
              target="_blank"
              title="terms of service"
            >'terms of service'</a></strong>'`
        ].join(" ")}
      />
    </p>
    <Actions>
      <Button on:click={makePayment} {working}>
        {$_("components.payment_form.buy_button.text", {
          default: "Pay {price}",
          values: {
            price: formattedPrice
          }
        })}
      </Button>
      <TextButton bold={true} on:click={close} size="1.6rem">
        {$_("components.payment_form.close_button.text", {
          default: "Close"
        })}
      </TextButton>
    </Actions>
  </fieldset>
</form>
